import { ShareAltOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { CreateSharedUrlDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath, Posthog, type PosthogEvent } from '@salescore/client-base'
import type { ViewConfigKpiParameter } from '@salescore/core'
import { getQueryParameter } from '@salescore/frontend-common'
import { App, Button } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { SheetSharedUrlParameter } from '../domain/applySharedUrlToView'

type KpiPivotParameter = ViewConfigKpiParameter & { kpiPivotPresetName: string | undefined }

/**
 * 表示中の URL から検索語を読み取り、共有 URL の末尾に付ける検索語クエリを組み立てる。
 * 検索語を持つのはシートのみで、検索していない場合は付与しない。
 */
const buildSearchQueryUrlPart = (viewType: ViewFieldsFragment['type']): string => {
  const searchQuery = getQueryParameter('searchQuery')
  return viewType === 'sheet' && isNotNullish(searchQuery) && searchQuery !== ''
    ? `&searchQuery=${encodeURIComponent(searchQuery)}`
    : ''
}

/**
 * ダッシュボード・シート共通の共有リンク発行ボタン。
 * 呼び出し側が組み立てた parameter で SharedUrl を作成し、閲覧用 URL をクリップボードにコピーする。
 */
export const ShareViewButton = ({
  view,
  parameter,
  posthogEvent,
}: {
  view: ViewFieldsFragment
  parameter: SheetSharedUrlParameter | KpiPivotParameter
  posthogEvent: PosthogEvent
}): ReactElement => {
  const [createSharedUrlMutation] = useMutation(CreateSharedUrlDocument)
  const { message } = App.useApp()
  const { t } = useTranslation()
  const onClick = async (): Promise<void> => {
    Posthog.track(posthogEvent, {
      organizationId: getOrganizationIdFromPath(),
      viewId: view.id,
    })
    // 失敗内容の Bugsnag 通知は Apollo の errorLink が行うため、ここでは握り潰してユーザー向けの警告表示だけ行う
    const result = await createSharedUrlMutation({
      variables: {
        organizationId: getOrganizationIdFromPath(),
        sharedUrl: {
          viewId: view.id,
          parameter,
          type: view.type,
        },
      },
    }).catch(() => null)
    const sharedUrlId = result?.data?.createSharedUrl?.id
    if (sharedUrlId === undefined) {
      void message.warning(t(`共有用URLを生成できませんでした`))
      return
    }
    const searchQueryPart = buildSearchQueryUrlPart(view.type)
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: TanStack Routerの情報を利用してないので不適切だがここまで簡単に渡せない
    const topPath = `/o/${getOrganizationIdFromPath()}/v2?viewId=${view.id}&shareId=${sharedUrlId}${searchQueryPart}`
    const shareURL = location.origin + topPath
    try {
      await navigator.clipboard.writeText(shareURL)
    } catch {
      void message.warning(t(`共有用URLのコピーに失敗しました`))
      return
    }
    void message.info(t(`共有用URLをクリップボードにコピーしました`))
  }
  return (
    <Button
      type="text"
      aria-label={t(`共有リンク`)}
      onClick={() => {
        void onClick()
      }}
      icon={<ShareAltOutlined />}
    />
  )
}
